import { useEffect, useState } from 'react';
import { Modal } from '../../../components/Modal';
import { Button } from '../../../components/Button';
import { Spinner } from '../../../components/Spinner';
import { searchCustomers } from '../../../api/endpoints';
import type { Customer } from '../../../types/api';

interface Props {
  open: boolean;
  initialQuery?: string;
  departmentCode?: string;
  onClose: () => void;
  onSelect: (code: string, name: string, staffCode?: string, nameEditable?: boolean, abbr?: string) => void;
}

const PER_PAGE = 20;

export function CustomerSearchModal({ open, initialQuery, departmentCode, onClose, onSelect }: Props) {
  const [query, setQuery] = useState(initialQuery ?? '');
  const [results, setResults] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterByDept, setFilterByDept] = useState(true);

  useEffect(() => {
    if (open && initialQuery) {
      setQuery(initialQuery);
      void doSearch(initialQuery, 1, filterByDept);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialQuery]);

  async function doSearch(q: string, p: number, useDeptFilter = filterByDept) {
    if (!q) return;
    setLoading(true);
    try {
      const dept = useDeptFilter ? (departmentCode ?? '') : '';
      const data = await searchCustomers(q, p, PER_PAGE, dept);
      setResults(data.results);
      setTotal(data.total);
      setPage(p);
    } catch (e) {
      alert(`検索失敗: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }

  const totalPages = Math.ceil(total / PER_PAGE);

  return (
    <Modal open={open} onClose={onClose} title="顧客マスタ検索" widthClass="max-w-4xl">
      <div className="flex gap-2 mb-3 items-center">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && doSearch(query, 1)}
          placeholder="顧客名・電話番号・FAXで検索"
          className="flex-1 border border-border rounded px-3 py-2 text-sm"
        />
        <Button onClick={() => doSearch(query, 1)}>検索</Button>
      </div>
      {departmentCode && (
        <label className="flex items-center gap-1.5 mb-2 text-xs text-muted cursor-pointer select-none">
          <input
            type="checkbox"
            checked={filterByDept}
            onChange={(e) => {
              const next = e.target.checked;
              setFilterByDept(next);
              if (query) void doSearch(query, 1, next);
            }}
          />
          拠点で絞り込む
        </label>
      )}

      {loading ? (
        <Spinner />
      ) : results.length === 0 ? (
        <p className="text-muted">検索してください</p>
      ) : (
        <>
          <p className="text-xs text-muted mb-1">{total}件中 {(page - 1) * PER_PAGE + 1}〜{Math.min(page * PER_PAGE, total)}件</p>
          <div className="overflow-auto max-h-[60vh]">
            <table>
              <thead>
                <tr>
                  <th>コード</th>
                  <th>名称</th>
                  <th>略称</th>
                  <th>電話</th>
                  <th>FAX</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <tr key={r.code}>
                    <td className="text-xs font-mono">{r.code}</td>
                    <td>{r.name1}</td>
                    <td>{r.abbr}</td>
                    <td className="text-xs">{r.phone}</td>
                    <td className="text-xs">{r.fax}</td>
                    <td>
                      <Button onClick={() => onSelect(r.code, r.name1 || r.abbr, r.staff_code, r.name_editable, r.abbr)}>選択</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex gap-2 items-center mt-2">
              <Button
                className="text-xs px-2 py-1"
                disabled={page <= 1}
                onClick={() => void doSearch(query, page - 1)}
              >前へ</Button>
              <span className="text-xs text-muted">{page} / {totalPages}</span>
              <Button
                className="text-xs px-2 py-1"
                disabled={page >= totalPages}
                onClick={() => void doSearch(query, page + 1)}
              >次へ</Button>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
